import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationPanel = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=15`);
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-read`, null, { params: { mark_all: true } });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'match_report': return '📋';
      case 'pdp_update': return '🎯';
      case 'injury_update': return '🏥';
      case 'milestone_completed': return '🏆';
      default: return '🔔';
    }
  };

  const getTypeBg = (type) => {
    switch (type) {
      case 'match_report': return 'bg-blue-50';
      case 'pdp_update': return 'bg-emerald-50';
      case 'injury_update': return 'bg-red-50';
      case 'milestone_completed': return 'bg-amber-50';
      default: return 'bg-gray-50 dark:bg-gray-900';
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-panel">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-800 transition-colors"
        data-testid="notification-bell"
      >
        <span className="text-lg">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-full top-0 ml-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-[100] max-h-[70vh] overflow-hidden" data-testid="notification-dropdown">
          <div className="p-3 border-b flex items-center justify-between">
            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Notifications</span>
            {unreadCount > 0 && (
              <Button size="sm" variant="ghost" className="text-xs text-emerald-600" onClick={markAllRead} data-testid="mark-all-read">
                Mark all read
              </Button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[calc(70vh-50px)]">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-500">No notifications yet</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`p-3 border-b border-gray-50 hover:bg-gray-50 dark:bg-gray-900 transition-colors ${!n.read ? 'bg-blue-50/30' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex gap-2">
                    <span className={`w-8 h-8 rounded-lg ${getTypeBg(n.type)} flex items-center justify-center flex-shrink-0`}>
                      {getTypeIcon(n.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} truncate`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 truncate">{n.message}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
